import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Entre ton email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error("Erreur, reessaie plus tard");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden" 
      data-testid="forgot-password-page"
    >
      {/* Background Image with Dark Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=1920&q=80"
          alt="Ocean Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628]/95 via-[#0d1f3c]/90 to-[#1a365d]/85" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#C9A227]/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-[#16233B]/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <h1 className="text-2xl font-bold text-white font-heading tracking-tight">
              {sent ? "Email envoye !" : "Mot de passe oublie"}
            </h1>
            <p className="text-white/50 text-sm mt-2 text-center">
              {sent
                ? "Si cet email est associe a un compte, tu recevras un lien de reinitialisation."
                : "Entre ton email pour recevoir un lien de reinitialisation"
              }
            </p>
          </div>

          {sent ? (
            <div className="space-y-4">
              <p className="text-sm text-white/40 text-center">
                Verifie ta boite de reception et tes spams. Le lien est valable 1 heure.
              </p>
              <Button
                onClick={() => navigate("/login")}
                data-testid="back-to-login-btn"
                className="w-full h-12 bg-gradient-to-r from-[#C9A227] to-[#B8860B] hover:from-[#D4AF37] hover:to-[#C9A227] text-[#0a1628] font-semibold rounded-xl shadow-lg shadow-[#C9A227]/20"
              >
                Retour a la connexion
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/70">Email</label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ton@email.com"
                  className="h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#C9A227] focus-visible:border-[#C9A227]/50"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-btn"
                className="w-full h-12 bg-gradient-to-r from-[#C9A227] to-[#B8860B] hover:from-[#D4AF37] hover:to-[#C9A227] text-[#0a1628] font-semibold rounded-xl shadow-lg shadow-[#C9A227]/20 active:scale-[0.98] transition-all"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#0a1628]/30 border-t-[#0a1628] rounded-full animate-spin" />
                ) : (
                  "Envoyer le lien"
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/login")}
                data-testid="back-to-login-link"
                className="w-full text-white/50 hover:text-white hover:bg-white/5"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour a la connexion
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
